import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, RefreshCw, CheckCircle, XCircle, AlertCircle, Send } from 'lucide-react';
import { pushLocalNotification } from '@/hooks/localNotifications';
import { usePushNotificationActions } from '@/hooks/usePushNotificationActions';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export const ProfileNotifications: React.FC = () => {
  const { t, i18n } = useTranslation('profile');
  const { status, tokens, isLoading, checkStatus, forceReRegister } = usePushNotificationActions();
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleTestNotification = async () => {
    setIsTestingNotification(true);
    try {
      await pushLocalNotification(
        '🔔 Test notification',
        t('notifications.testNotificationBody', { defaultValue: 'Vous recevez bien les notifications !' })
      );
      toast.success(t('notifications.testSuccess'));
    } catch (error) {
      console.error('Test notification error:', error);
      toast.error(t('notifications.testError'));
    } finally {
      setIsTestingNotification(false);
    }
  };

  const handleReRegister = async () => {
    const result = await forceReRegister();
    
    if (result.success) {
      toast.success(t('notifications.reregisterSuccess'));
    } else {
      toast.error(t('notifications.reregisterError', { error: result.error }));
    }
  };

  const getPermissionBadge = () => {
    switch (status.permissionStatus) {
      case 'granted':
        return (
          <Badge variant="default" className="bg-green-500/20 text-green-700 dark:text-green-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            {t('notifications.permissionGranted')}
          </Badge>
        );
      case 'denied':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            {t('notifications.permissionDenied')}
          </Badge>
        );
      case 'prompt':
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t('notifications.permissionPrompt')}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {t('notifications.permissionUnknown')}
          </Badge>
        );
    }
  };

  const getRegistrationStatus = () => {
    if (tokens.length > 0) {
      const latestToken = tokens[0];
      const registeredDate = latestToken.inserted_at 
        ? format(new Date(latestToken.inserted_at), 'dd MMM yyyy', { 
            locale: i18n.language === 'fr' ? fr : undefined 
          })
        : null;

      return (
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-sm text-muted-foreground">
            {t('notifications.registeredOn', { date: registeredDate || '-' })}
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <XCircle className="h-4 w-4 text-destructive" />
        <span className="text-sm text-muted-foreground">
          {t('notifications.notRegistered')}
        </span>
      </div>
    );
  };

  if (!status.isNative) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <CardTitle>{t('notifications.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('notifications.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Permission Status */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{t('notifications.permissionStatus')}</h4>
          {getPermissionBadge()}
        </div>

        {/* Registration Status */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{t('notifications.deviceStatus')}</h4>
          {getRegistrationStatus()}
        </div>

        {/* Test Notification Button */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{t('notifications.testTitle')}</h4>
          <Button
            onClick={handleTestNotification}
            disabled={isTestingNotification}
            className="w-full"
            variant="secondary"
          >
            {isTestingNotification ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {t('notifications.testButton')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {t('notifications.testHint')}
          </p>
        </div>

        {/* Re-register Button */}
        <div className="pt-4 border-t space-y-2">
          <h4 className="text-sm font-medium">{t('notifications.troubleshootTitle')}</h4>
          <Button
            onClick={handleReRegister}
            disabled={isLoading}
            className="w-full"
            variant="outline"
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {t('notifications.reregisterButton')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {t('notifications.reregisterHint')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
